/**
 * Senden mit optimistischer Anzeige: ausstehende Nachricht (Nonce) → ggf. Anhänge hochladen (Fortschritt,
 * Abbrechen) → POST → bestätigte Nachricht übernehmen. Fehler bleiben als „fehlgeschlagen“ mit
 * „Erneut senden“ stehen; Slowmode setzt die Abklingzeit im Composer.
 */
import type { MessageDto } from '@lagerfeuer/shared';
import { ApiError, errorMessage } from '../../lib/api';
import { messagesApi } from '../../lib/endpoints';
import { ingestMessage } from '../../realtime/reducer';
import { appState } from '../../store/app';
import { useMessagesUi } from './state';

export interface DraftFile {
  /** Lokale ID (Liste im Composer). */
  id: string;
  file: File;
  spoiler: boolean;
  description?: string;
}

export interface SendInput {
  channelId: string;
  content: string;
  replyToId?: string | null;
  files?: DraftFile[];
  stickerIds?: string[];
  flags?: number;
  /** Slowmode des Kanals in Sekunden, falls er für den Nutzer gilt (Abklingzeit nach dem Senden). */
  slowmodeSeconds?: number;
}

/** Nutzdaten ausstehender Nachrichten (für „Erneut senden“). */
const payloads = new Map<string, SendInput>();

let nonceSeq = 0;
/** Eindeutige Nonce (Ziffern, ≤ 25 Zeichen wie üblich). */
export function makeNonce(): string {
  nonceSeq = (nonceSeq + 1) % 1000;
  return `${Date.now()}${String(nonceSeq).padStart(3, '0')}${Math.floor(Math.random() * 1000)}`;
}

export function describeSendError(e: unknown): string {
  if (e instanceof ApiError) {
    switch (e.code) {
      case 'SLOWMODE': {
        const secs = retryAfterSeconds(e);
        return secs ? `Langsamer Modus – du kannst in ${secs} s wieder schreiben.` : 'Langsamer Modus – bitte kurz warten.';
      }
      case 'CHANNEL_READONLY':
        return 'Dieser Kanal ist schreibgeschützt (gespiegelt).';
      case 'THREAD_LOCKED':
        return 'Dieser Thread ist gesperrt.';
      case 'RATE_LIMITED':
        return 'Zu viele Nachrichten in kurzer Zeit – bitte kurz warten.';
      case 'PAYLOAD_TOO_LARGE':
        return 'Die Datei ist zu groß.';
      case 'UNSUPPORTED_MEDIA_TYPE':
        return 'Dieser Dateityp wird nicht unterstützt.';
      case 'NETWORK':
        return 'Netzwerkfehler – keine Verbindung zum Server.';
      default:
        return errorMessage(e);
    }
  }
  if (e instanceof TypeError) return 'Netzwerkfehler – keine Verbindung zum Server.';
  return errorMessage(e);
}

export function retryAfterSeconds(e: ApiError): number | null {
  const raw = (e.body.details as Record<string, unknown> | undefined)?.retryAfter;
  const v = Array.isArray(raw) ? Number(raw[0]) : Number(raw);
  return Number.isFinite(v) && v > 0 ? Math.ceil(v) : null;
}

/** Bestätigte Nachricht übernehmen (falls das Gateway-Ereignis noch nicht da war; idempotent). */
export function acceptOwnMessage(msg: MessageDto, nonce: string): void {
  appState().update((d) => {
    ingestMessage(d, msg, d.me?.id ?? null, nonce);
    // Auch ohne Autor-Abgleich (z. B. Webhook-Antwort) die ausstehende Nachricht entfernen.
    const list = d.pending[msg.channelId];
    if (list) d.pending[msg.channelId] = list.filter((p) => p.nonce !== nonce);
  });
}

/**
 * Anhänge einzeln hochladen – eine Anfrage je Datei (R7a K7). So bleibt jede Anfrage unter dem Body-Limit der
 * Proxys (Caddy/nginx ~2,2 GiB), auch wenn mehrere große Dateien zusammen darüber lägen. Der Fortschritt wird über
 * alle Dateien summiert. Schlägt eine Datei fehl, werden die schon hochgeladenen (noch unverknüpften) wieder
 * gelöscht (best effort; sonst räumt der Server sie nach `orphanUploadHours` weg).
 */
export async function uploadDraftFiles(
  channelId: string,
  files: DraftFile[],
  opts: { signal?: AbortSignal; onProgress?: (loaded: number, total: number) => void } = {},
): Promise<string[]> {
  const total = files.reduce((n, f) => n + f.file.size, 0);
  const ids: string[] = [];
  let done = 0;
  try {
    for (const f of files) {
      const size = f.file.size;
      const uploaded = await messagesApi.upload(channelId, [{ file: f.file, spoiler: f.spoiler, description: f.description }], {
        signal: opts.signal,
        onProgress: (loaded) => opts.onProgress?.(done + Math.min(loaded, size), total),
      });
      ids.push(...uploaded.map((a) => a.id));
      done += size;
      opts.onProgress?.(done, total);
    }
  } catch (e) {
    await Promise.all(ids.map((id) => messagesApi.deleteAttachment(id).catch(() => undefined)));
    throw e;
  }
  return ids;
}

async function run(input: SendInput, nonce: string): Promise<MessageDto | null> {
  const ui = useMessagesUi.getState();
  try {
    let attachmentIds: string[] | undefined;
    if (input.files?.length) {
      const controller = new AbortController();
      const total = input.files.reduce((n, f) => n + f.file.size, 0);
      ui.setUpload(nonce, { loaded: 0, total, abort: () => controller.abort() });
      attachmentIds = await uploadDraftFiles(input.channelId, input.files, {
        signal: controller.signal,
        onProgress: (loaded, t) => useMessagesUi.getState().setUpload(nonce, { loaded, total: t, abort: () => controller.abort() }),
      });
      ui.setUpload(nonce, null);
    }
    const msg = await messagesApi.send(input.channelId, {
      content: input.content || undefined,
      nonce,
      replyToId: input.replyToId ?? undefined,
      attachmentIds,
      stickerIds: input.stickerIds?.length ? input.stickerIds : undefined,
      flags: input.flags || undefined,
    });
    payloads.delete(nonce);
    acceptOwnMessage(msg, nonce);
    if (input.slowmodeSeconds) ui.setCooldown(input.channelId, Date.now() + input.slowmodeSeconds * 1000);
    return msg;
  } catch (e) {
    useMessagesUi.getState().setUpload(nonce, null);
    if (e instanceof DOMException && e.name === 'AbortError') {
      discardPending(input.channelId, nonce);
      return null;
    }
    if (e instanceof ApiError && e.code === 'SLOWMODE') {
      const secs = retryAfterSeconds(e) ?? input.slowmodeSeconds ?? 5;
      ui.setCooldown(input.channelId, Date.now() + secs * 1000);
    }
    appState().setPending(input.channelId, nonce, { status: 'failed', error: describeSendError(e) });
    return null;
  }
}

/** Nachricht senden (optimistisch). Liefert die bestätigte Nachricht oder null bei Fehler/Abbruch. */
export function sendMessage(input: SendInput, nonce = makeNonce()): Promise<MessageDto | null> {
  payloads.set(nonce, input);
  appState().addPending({
    nonce,
    channelId: input.channelId,
    content: input.content,
    replyToId: input.replyToId ?? null,
    createdAt: new Date().toISOString(),
    status: 'sending',
  });
  return run(input, nonce);
}

export function retryPending(channelId: string, nonce: string): Promise<MessageDto | null> {
  const p = appState().pending[channelId]?.find((x) => x.nonce === nonce);
  const input = payloads.get(nonce) ?? (p ? { channelId, content: p.content, replyToId: p.replyToId } : null);
  if (!input) return Promise.resolve(null);
  appState().setPending(channelId, nonce, { status: 'sending', error: undefined });
  return run(input, nonce);
}

export function discardPending(channelId: string, nonce: string): void {
  payloads.delete(nonce);
  appState().setPending(channelId, nonce, null);
}

/** Dateien einer ausstehenden Nachricht (Anzeige im Platzhalter). */
export function pendingFiles(nonce: string): DraftFile[] {
  return payloads.get(nonce)?.files ?? [];
}
